import { defineStore } from 'pinia'
import { useCommonMetricsStore } from './common-metrics'
import { PRODUCT_REVIEWS_LIMIT } from '~/constants/common'
import { buildQueryString } from '~/utils/http'
import type { ServerListResponse } from '~/types/api/response.types'
import type { CreateReviewAttachmentsPayload, CreateReviewPayload, GetProductReviewParams, Review, ReviewAttachment, ReviewFeedbackReaction, ReviewMeta, UpdateReactionToReview } from '~/types/review'
import {REVIEW_ATTACHMENT_IMAGE, REVIEW_ATTACHMENT_VIDEO} from '~/constants/reviews'

interface State {
  fetching: boolean
  createProcessing: boolean
  attachmentsProcessing: boolean
  showReviewCaptcha: boolean
  showReplyCaptcha: boolean
  count: number
  reviews: Review[]
  serverErrors: { [key: string]: any }
  onFocusedReview: Review | null
  metaFetching: boolean
  meta: ReviewMeta | null
  limit: number
  reviewsVideo: Review[]
  reviewsImage: Review[]
  reviewsMediaFetching: boolean
  selectedFeedbackReaction: { [key: string]: number }
  uploadProgress: number[]
  triggerSuccessModal: boolean
}

export const useProductReviewsStore = defineStore('productReviews', {
  state: (): State => ({
    fetching: false,
    createProcessing: false,
    attachmentsProcessing: false,
    showReviewCaptcha: false,
    showReplyCaptcha: false,
    count: 0,
    reviews: [],
    serverErrors: {},
    onFocusedReview: null,
    metaFetching: false,
    meta: null,
    limit: PRODUCT_REVIEWS_LIMIT,
    reviewsMediaFetching: false,
    reviewsVideo: [],
    reviewsImage: [],
    selectedFeedbackReaction: {},
    uploadProgress: [],
    triggerSuccessModal: false
  }),
  getters: {
    hasReviews(state) {
      return state.reviews.length > 0
    },
    hasReviewsMedia(state) {
      return (state.reviewsVideo.length || state.reviewsImage.length) > 0
    }
  },
  actions: {
    async getReviews({ productUuid, offset = 0, limit }: GetProductReviewParams) {
      const { $api } = useNuxtApp()

      this.fetching = true
      this.serverErrors = {}

      if (limit === undefined) {
        limit = this.limit
      }

      return new Promise((resolve, reject) => {
        $api<ServerListResponse<Review>>(
          `/api/catalog/products/${productUuid}/reviews/?${buildQueryString({ offset, limit })}`
        )
          .then((response) => {
            if (offset > 0) {
              this.reviews = [...this.reviews, ...response.results]
              this.count = response.count
            } else {
              this.count = response.count || 0
              this.reviews = response.results || []
            }

            resolve(response)
          })
          .catch(reject)
          .finally(() => {
            this.fetching = false
          })
      })
    },

    async getReviewsLimit({ productUuid, reviewUuid }: { productUuid: string, reviewUuid: string }): Promise<number> {
      if (reviewUuid === undefined) {
        return Promise.resolve(this.limit)
      }

      const { $api } = useNuxtApp()

      return new Promise((resolve, reject) => {
        $api<{ page: number, uuid: string }>(
          `/api/catalog/products/${productUuid}/reviews/${reviewUuid}/page?limit=${PRODUCT_REVIEWS_LIMIT}`
        ).then(({ page }) => {
          resolve(page * this.limit)
        }).catch(reject)
      })
    },

    async createReview({ productUuid, formData, successCallback, type }: CreateReviewPayload) {
      const { $api } = useNuxtApp()

      this.createProcessing = true
      this.serverErrors = {}

      const commonMetricsStore = useCommonMetricsStore()

      try {
        const formDataParams = new FormData()

        formDataParams.append('comment', formData.comment)
        if (formData.rating) {
          formDataParams.append('rating', formData.rating.toString())
        }
        if (formData.author_username) {
          formDataParams.append('author_username', formData.author_username)
        }
        if (formData.parent) {
          formDataParams.append('parent', formData.parent)
        }

        const response = await $api<Review>(
          `/api/catalog/products/${encodeURIComponent(productUuid)}/reviews/`,
          { method: 'POST', body: formDataParams }
        )

        if (this.onFocusedReview && this.onFocusedReview.children) {
          // if reply
          this.onFocusedReview.children = [
            response,
            ...this.onFocusedReview.children
          ]
        }

        if (!formData.attachments?.length) {
          this.onFocusedReview = null
        }

        // Після створення відгуку тригерим модалку "Дякуємо за відгук" (відгук-відповідь ігноруємо)
        if (!formData.parent) {
          this.handleTriggerSuccessModal()
        }

        commonMetricsStore.addMetricEvent({
          element: 'ms[product-reviews-add]',
          event_type: 'submit',
          event_content: ''
        })

        successCallback?.(response.uuid)

        this.getReviewsMeta(productUuid)

        if (this.showReviewCaptcha || this.showReplyCaptcha) {
          this.showReviewCaptcha = false
          this.showReplyCaptcha = false
        }
      } catch (e: any) {
        this.serverErrors = e.response.data

        if (e.response.status === 409) {
          this.showReviewCaptcha = false
          this.showReplyCaptcha = false

          if (type === 'review') {
            this.showReviewCaptcha = true
          } else if (type === 'reply') {
            this.showReplyCaptcha = true
          }
        }
      } finally {
        this.createProcessing = false
      }
    },

    async createReviewWithRecaptcha({ productUuid, formData, successCallback, recaptchaToken, type }: CreateReviewPayload) {
      const { $api, $i18n, $toast } = useNuxtApp()

      this.createProcessing = true
      this.serverErrors = {}

      const { success } = await $api<{ success: boolean }>('/api/common/recaptcha/', {
        method: 'POST',
        body: { token: recaptchaToken }
      })

      if (success) {
        this.createReview({ productUuid, formData, successCallback, type })
      } else {
        this.createProcessing = false
        $toast.error($i18n.t('common.captcha_failure'))
      }
    },

    async attachMediaToReview({ productUuid, reviewUuid, formData, successCallback }: CreateReviewAttachmentsPayload) {
      const { $toast, $i18n } = useNuxtApp()
      const { progressFetch } = useApiWithProgress()

      this.attachmentsProcessing = true
      this.uploadProgress = new Array(formData.attachments.length).fill(0)
      const attachmentsFormData = new FormData()

      try {
        for (let i = 0; i < formData.attachments.length; i++) {
          if (this.uploadProgress[i] >= 100) {
            continue
          }
          const file = formData.attachments[i]
          attachmentsFormData.append('attachments', file)

          const attachmentsResponse = await progressFetch<{ count: number, results: ReviewAttachment[] }>(
            `/api/catalog/products/${encodeURIComponent(productUuid)}/reviews/${reviewUuid}/attachments/`,
            {
              method: 'POST',
              body: attachmentsFormData,
              onUploadProgress: (progressEvent) => {
                if (progressEvent.total) {
                  this.uploadProgress = this.uploadProgress.map((item, index) => {
                    return index === i ? progressEvent.percent : item
                  })
                }
              }
            }
          )
          this.uploadProgress[i] = 100
          attachmentsFormData.delete('attachments')

          if (this.onFocusedReview && this.onFocusedReview.children) {
            this.onFocusedReview.children.forEach((review) => {
              if (review.uuid === reviewUuid) {
                review.attachments.push(...attachmentsResponse.results)
              }
            })
          }
        }

        successCallback()
        this.onFocusedReview = null
        this.uploadProgress = []
      } catch (e: any) {
        if (e.response?.status === 413) {
          return $toast.error($i18n.t('common.max_file_size', { count: 100 }))
        }
        this.serverErrors = e.response?.data
      } finally {
        this.attachmentsProcessing = false
      }
    },

    async getReviewsMeta(productId: string) {
      const { $api } = useNuxtApp()
      this.metaFetching = true

      try {
        const response = await $api<ReviewMeta>(`/api/catalog/products/${productId}/reviews/meta/`)

        this.meta = response
      } finally {
        this.metaFetching = false
      }
    },

    setOnFocusedReview(review: Review) {
      this.onFocusedReview = review
    },

    async addLikeToReview(params: UpdateReactionToReview) {
      try {
        await this.handleReviewAction(params, 'like')
        const isLiked = !params.cancel ? 1 : 0

        this.updateFeedbackReaction(params.reviewId, isLiked)
      } catch {
        this.updateFeedbackReaction(params.reviewId)
      }
    },

    async addDislikeToReview(params: UpdateReactionToReview) {
      try {
        await this.handleReviewAction(params, 'dislike')
        const isDisliked = !params.cancel ? -1 : 0

        this.updateFeedbackReaction(params.reviewId, isDisliked)
      } catch {
        this.updateFeedbackReaction(params.reviewId)
      }
    },

    updateFeedbackReactionInReviews({ reviewId, parentUuids }: { reviewId: string, parentUuids: string[] | null }, reaction: ReviewFeedbackReaction, sourceToChange: Review[]) {
      const findNestedReview = (reviews: Review[], uuids: string[]) => {
        return uuids.reduce((currentParent, uuid) => {
          return currentParent.find((review) => review.uuid === uuid)?.children || []
        }, reviews)
      }

      const parentReview = parentUuids && parentUuids.length > 0
        ? findNestedReview(sourceToChange, parentUuids)
        : sourceToChange

      const review = parentReview.find((review) => review.uuid === reviewId)

      if (review) {
        review.likes = reaction.likes || 0
        review.dislikes = reaction.dislikes || 0
      }
    },

    updateFeedbackReaction(reviewId: string, value: number = 0) {
      this.selectedFeedbackReaction = {
        ...this.selectedFeedbackReaction,
        [reviewId]: value
      }
    },

    async handleReviewAction({ productUuid, reviewId, parentUuids, cancel = false }: UpdateReactionToReview, action: 'like' | 'dislike') {
      const { $api } = useNuxtApp()
      try {
        const apiUrl = `api/catalog/products/${productUuid}/reviews/${reviewId}/${action}/`
        const reviewFeedbackReaction = cancel
          ? await $api<ReviewFeedbackReaction>(apiUrl, { method: 'DELETE' })
          : await $api<ReviewFeedbackReaction>(apiUrl, { method: 'POST' })

        this.updateFeedbackReactionInReviews({
          reviewId,
          parentUuids
        }, reviewFeedbackReaction, this.reviews)

        this.updateFeedbackReactionInReviews({
          reviewId,
          parentUuids
        }, reviewFeedbackReaction, this.reviewsImage)

        this.updateFeedbackReactionInReviews({
          reviewId,
          parentUuids
        }, reviewFeedbackReaction, this.reviewsVideo)
      } catch (e: any) {
        if (e.response?.status === 400 && !cancel) {
          this.handleReviewAction({
            productUuid,
            reviewId,
            parentUuids,
            cancel: true
          }, action)
        }

        throw e
      }
    },

    async getReviewAttachments(productUuid: string) {
      this.reviewsMediaFetching = true

      const filterReviewed = (results: Review[]) => {
        const seenUuids = new Set<string>()

        return results.filter((review) => {
          if (seenUuids.has(review.uuid)) {
            return false
          }
          seenUuids.add(review.uuid)
          return true
        })
      }

      const params = {
        attachment_type: REVIEW_ATTACHMENT_VIDEO,
        offset: 0,
        limit: 99
      }

      const { $api } = useNuxtApp()

      return Promise.all([
        $api<ServerListResponse<Review>>(`/api/catalog/products/${productUuid}/reviews/`, { params }),
        $api<ServerListResponse<Review>>(`/api/catalog/products/${productUuid}/reviews/`, {
          params: {
            ...params,
            attachment_type: REVIEW_ATTACHMENT_IMAGE
          }
        })
      ])
        .then(([reviewsVideo, reviewsImage]) => {
          this.reviewsVideo = filterReviewed(reviewsVideo.results)
          this.reviewsImage = filterReviewed(reviewsImage.results)
        })
        .catch(() => {
          this.reviewsVideo = []
          this.reviewsImage = []
        })
        .finally(() => {
          this.reviewsMediaFetching = false
        })
    },

    handleTriggerSuccessModal() {
      this.triggerSuccessModal = true
    },

    resetSuccessModalTrigger() {
      this.triggerSuccessModal = false
    }
  }
})
